import React, { useState } from "react";
import { toast } from "sonner";
import { History, X, RotateCcw, Loader2, GitCompareArrows, ChevronUp } from "lucide-react";
import { diffWords } from "diff";
import { apiClient, formatApiError } from "../../lib/api";

const fmt = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) +
            ", " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";
    } catch {
        return iso;
    }
};

const excerpt = (v) => {
    if (!v || typeof v !== "object") return "";
    const s =
        v.headline ||
        v.subject ||
        v.content ||
        (Array.isArray(v.highlights) && v.highlights.map((h) => h.title).filter(Boolean).join(", ")) ||
        JSON.stringify(v);
    return s.length > 110 ? s.slice(0, 110) + "…" : s;
};

const flatten = (v) => {
    const out = [];
    if (!v || typeof v !== "object") return out;
    if (v.headline != null) out.push(["Überschrift", v.headline]);
    if (v.intro != null) out.push(["Einleitung", v.intro]);
    (v.paragraphs || []).forEach((p, i) => out.push([`Absatz ${i + 1}`, p]));
    if (v.subject != null) out.push(["Betreff", v.subject]);
    if (v.body != null) out.push(["E-Mail-Text", v.body]);
    if (v.content != null) out.push(["Text", v.content]);
    (v.highlights || []).forEach((c, i) => out.push([`Großer Vorteil ${i + 1}`, `${c.title || ""} – ${c.text || ""}`]));
    (v.additional || []).forEach((c, i) => out.push([`Weiterer Vorteil ${i + 1}`, `${c.title || ""} – ${c.text || ""}`]));
    (v.cards || []).forEach((c, i) => out.push([`Karte ${i + 1}`, `${c.title || ""} – ${c.text || ""}`]));
    if (v.stat1_number != null || v.stat1_label != null)
        out.push(["Info-Box 1", `${v.stat1_number || ""}${v.stat1_suffix || ""} – ${v.stat1_label || ""}`]);
    if (v.stat2_title != null || v.stat2_label != null)
        out.push(["Info-Box 2", `${v.stat2_title || ""} – ${v.stat2_label || ""}`]);
    return out;
};

function FieldDiff({ oldText, newText }) {
    const parts = diffWords(oldText || "", newText || "");
    return (
        <div className="text-xs leading-relaxed whitespace-pre-wrap">
            {parts.map((p, i) =>
                p.added ? (
                    <span key={i} className="bg-emerald-100 text-emerald-800 rounded px-0.5">{p.value}</span>
                ) : p.removed ? (
                    <span key={i} className="bg-red-100 text-red-700 line-through rounded px-0.5">{p.value}</span>
                ) : (
                    <span key={i} className="text-slate-500">{p.value}</span>
                )
            )}
        </div>
    );
}

function VersionDiff({ newer, older }) {
    if (!older) {
        return (
            <div className="text-xs text-slate-500 italic">
                Erste erfasste Fassung – keine ältere Version zum Vergleichen vorhanden.
            </div>
        );
    }
    const newFields = flatten(newer);
    const oldMap = Object.fromEntries(flatten(older));
    const newLabels = new Set(newFields.map(([l]) => l));
    const changed = [];
    newFields.forEach(([label, text]) => {
        if ((oldMap[label] || "") !== (text || "")) changed.push([label, oldMap[label] || "", text]);
    });
    flatten(older).forEach(([label, text]) => {
        if (!newLabels.has(label)) changed.push([label, text, ""]);
    });
    if (changed.length === 0) {
        return <div className="text-xs text-slate-500 italic">Keine Text-Änderungen gegenüber der vorherigen Fassung.</div>;
    }
    return (
        <div className="grid gap-2.5">
            <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300 align-middle mr-1"></span>neu</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300 align-middle mr-1"></span>entfernt</span>
            </div>
            {changed.map(([label, oldText, newText]) => (
                <div key={label}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">{label}</div>
                    <FieldDiff oldText={oldText} newText={newText} />
                </div>
            ))}
        </div>
    );
}

export const HistoryButton = ({ historyKey, onRestore }) => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [diffIdx, setDiffIdx] = useState(null);

    const openDialog = async () => {
        setOpen(true);
        setItems(null);
        setDiffIdx(null);
        try {
            const { data } = await apiClient.get(`/admin/history/${encodeURIComponent(historyKey)}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            setItems([]);
        }
    };

    const restore = (item) => {
        onRestore(item.value);
        setOpen(false);
        toast.success("Alte Fassung geladen – zum Übernehmen auf „Speichern“ klicken.");
    };

    return (
        <>
            <button
                data-testid={`history-btn-${historyKey}`}
                onClick={openDialog}
                title="Änderungs-Historie"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-md border border-moebus-border"
            >
                <History size={14} /> Verlauf
            </button>
            {open && (
                <div
                    data-testid={`history-dialog-${historyKey}`}
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setOpen(false)}
                >
                    <div className="bg-white w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-lg shadow-2xl">
                        <div className="sticky top-0 bg-white border-b border-moebus-border px-5 py-3.5 flex items-center justify-between">
                            <h3 className="font-heading font-bold text-moebus-deep flex items-center gap-2">
                                <History size={16} /> Änderungs-Historie
                            </h3>
                            <button
                                data-testid="history-close"
                                onClick={() => setOpen(false)}
                                className="text-slate-400 hover:text-slate-700"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4">
                            {items === null && (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="animate-spin text-slate-400" />
                                </div>
                            )}
                            {items !== null && items.length === 0 && (
                                <div className="text-sm text-slate-500 text-center py-8">
                                    Noch keine gespeicherten Fassungen. Ab jetzt wird jede Änderung beim Speichern festgehalten.
                                </div>
                            )}
                            {items !== null && items.length > 0 && (
                                <div className="grid gap-2">
                                    {items.map((item, idx) => (
                                        <div
                                            key={item.id}
                                            data-testid={`history-item-${idx}`}
                                            className="border border-moebus-border rounded-lg p-3"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-medium text-moebus-deep">
                                                        {fmt(item.saved_at)}
                                                        {idx === 0 && (
                                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">
                                                                Aktuell
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400">{item.changed_by}</div>
                                                    <div className="mt-1 text-xs text-slate-600 truncate">{excerpt(item.value)}</div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    <button
                                                        data-testid={`history-restore-${idx}`}
                                                        onClick={() => restore(item)}
                                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md"
                                                    >
                                                        <RotateCcw size={12} /> Laden
                                                    </button>
                                                    <button
                                                        data-testid={`history-diff-${idx}`}
                                                        onClick={() => setDiffIdx(diffIdx === idx ? null : idx)}
                                                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md ${
                                                            diffIdx === idx
                                                                ? "bg-slate-100 text-moebus-deep"
                                                                : "text-slate-500 hover:bg-slate-100"
                                                        }`}
                                                    >
                                                        {diffIdx === idx ? <ChevronUp size={12} /> : <GitCompareArrows size={12} />} Änderungen
                                                    </button>
                                                </div>
                                            </div>
                                            {diffIdx === idx && (
                                                <div
                                                    data-testid={`history-diff-view-${idx}`}
                                                    className="mt-3 pt-3 border-t border-moebus-border"
                                                >
                                                    <VersionDiff newer={item.value} older={items[idx + 1]?.value} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="mt-3 text-[11px] text-slate-400">
                                Die letzten 20 gespeicherten Fassungen. „Laden“ übernimmt die Fassung in den Editor –
                                erst nach Klick auf „Speichern“ ist sie auf der Website aktiv.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
